import { ReactElement } from 'react';
import ChevronIcon from 'assets/icons/chevron.svg?react';
import { useSelect, UseSelectStateChange } from 'downshift';

import Flex from 'components/layout/flex';
import Cell from 'components/layout/cell';
import { Image } from '../../image/Image';
import { Caption } from 'components/elements/caption/Caption';

import { DropdownItem } from './Dropdown.types';
import {
  DropdownContainer,
  DropdownLabel,
  DropdownButton,
  DropdownList,
  DropdownListItem,
  DropdownIcon,
} from './Dropdown.styles';

export interface DropdownProps<LabelT = string, ValueT = string> {
  id: string;
  items: Array<DropdownItem<LabelT, ValueT>>;
  label?: string;
  buttonTheme?: string;
  placeholder?: string;
  isFull?: boolean;
  selected?: DropdownItem<LabelT, ValueT>;
  onChange: (
    selectedItem: DropdownItem<LabelT, ValueT> | null | undefined
  ) => void;
}

export const Dropdown = <LabelT, ValueT>({
  id,
  items,
  label,
  selected,
  onChange,
  buttonTheme,
  placeholder,
  isFull,
}: DropdownProps<LabelT, ValueT>): ReactElement => {
  const {
    isOpen,
    getToggleButtonProps,
    getLabelProps,
    getMenuProps,
    highlightedIndex,
    getItemProps,
    selectedItem,
  } = useSelect<DropdownItem<LabelT, ValueT>>({
    items,
    itemToString: (selectedItem: DropdownItem<LabelT, ValueT> | null) =>
      (typeof selectedItem?.label === 'string' && selectedItem?.label) || '',
    selectedItem: selected,
    onSelectedItemChange: ({
      selectedItem,
    }: UseSelectStateChange<DropdownItem<LabelT, ValueT>>) =>
      onChange(selectedItem),
  });

  const renderItems = (items: Array<DropdownItem<LabelT, ValueT>>) => {
    return items.map((item: DropdownItem<LabelT, ValueT>, index: number) => {
      const itemId = `${id}_item_${item.value}`;
      return (
        <DropdownListItem
          {...getItemProps({
            item,
            index,
            selected: selectedItem?.value === item.value,
            id: itemId,
          })}
          data-testid={itemId}
          isHighlighted={highlightedIndex === index}
          key={itemId}
        >
          <Flex alignItems='center'>
            {item.iconUrl && (
              <Cell mr={4} width='35px'>
                <Image alt={label || ''} src={item.iconUrl} width={35} />
              </Cell>
            )}
            <Cell>
              <Caption>{typeof item.label === 'string' && item.label}</Caption>
            </Cell>
          </Flex>
        </DropdownListItem>
      );
    });
  };

  return (
    <Flex
      id={id}
      data-testid={id}
      flexDirection={['row']}
      width={isFull ? '100%' : undefined}
    >
      {label && (
        <DropdownLabel {...getLabelProps()} data-testid={`${id}_label`}>
          {label}
        </DropdownLabel>
      )}
      <DropdownContainer>
        <DropdownButton
          {...getToggleButtonProps()}
          isOpen={isOpen}
          data-testid={`${id}_button`}
          id={`${id}_button`}
          buttonTheme={buttonTheme}
        >
          <Flex alignItems='center'>
            {selectedItem?.iconUrl && (
              <Cell mr={4} width='25px'>
                <Image
                  alt={label || ''}
                  src={selectedItem.iconUrl}
                  width={25}
                />
              </Cell>
            )}
            <Cell>
              <Caption>
                {(typeof selectedItem?.label === 'string' &&
                  selectedItem?.label) ||
                  placeholder ||
                  '-'}
              </Caption>
            </Cell>
          </Flex>
          <DropdownIcon
            className={isOpen ? 'open' : ''}
            icon={ChevronIcon}
            width={16}
            height={16}
          />
        </DropdownButton>
        <DropdownList {...getMenuProps()} data-testid={`${id}_list`}>
          {isOpen && renderItems(items)}
        </DropdownList>
      </DropdownContainer>
    </Flex>
  );
};
